import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';

import { getProcess } from '../../transactions/transaction';

export const CLIENT_OPPORTUNITY_LISTING_TYPES = ['request-quote', 'pedir-cotizacion'];

export const LISTING_GROUP_AVAILABLE = 'available';
export const LISTING_GROUP_ACTIVE = 'active';
export const LISTING_GROUP_ARCHIVED = 'archived';

export const LISTING_STATUS_RECEIVING_OFFERS = 'receiving-offers';
export const LISTING_STATUS_PAST_DATE = 'past-date';
export const LISTING_STATUS_DRAFT = 'draft';
export const LISTING_STATUS_PENDING_APPROVAL = 'pending-approval';
export const LISTING_STATUS_IN_PROCESS = 'in-process';
export const LISTING_STATUS_COMPLETED = 'completed';
export const LISTING_STATUS_CANCELED = 'canceled';
export const LISTING_STATUS_EXPIRED = 'expired';
export const LISTING_STATUS_MANUALLY_ARCHIVED = 'manually-archived';
export const LISTING_STATUS_UNKNOWN = 'unknown';

export const CLIENT_OPPORTUNITY_TIME_ZONE = 'America/Puerto_Rico';
export const ARCHIVED_BY_AUTHOR_FIELD = 'archivedByAuthor';
export const ARCHIVED_AT_FIELD = 'archivedAt';

const REQUESTED_TIME_WINDOW_PATTERN = /^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/;

export const IN_PROCESS_TRANSACTION_STATES = [
  'offer-accepted',
  'delivered',
  'changes-requested',
];

export const COMPLETED_TRANSACTION_STATES = [
  'completed',
  'reviewed-by-customer',
  'reviewed-by-provider',
  'reviewed',
];

const KNOWN_TRANSACTION_STATES = [
  ...IN_PROCESS_TRANSACTION_STATES,
  ...COMPLETED_TRANSACTION_STATES,
  'inquiry',
  'quote-requested',
  'offer-pending',
  'update-pending',
  'customer-offer-pending',
  'pending-payment',
  'canceled',
  'payment-expired',
  'offer-rejected',
  'request-rejected',
];

const normalizeState = state =>
  typeof state === 'string' && state.includes('/') ? state.split('/').pop() : state;

export const isClientOpportunityListing = listing =>
  CLIENT_OPPORTUNITY_LISTING_TYPES.includes(listing?.attributes?.publicData?.listingType);

/**
 * Return the end of the requested service window as a real instant in Puerto Rico.
 * A window ending at or before its start (e.g. 23:00-00:00) ends on the following day.
 */
export const getClientOpportunityEnd = listing => {
  const { requestedDate, requestedTimeWindow } = listing?.attributes?.publicData || {};
  const windowParts =
    typeof requestedTimeWindow === 'string'
      ? requestedTimeWindow.match(REQUESTED_TIME_WINDOW_PATTERN)
      : null;

  if (typeof requestedDate !== 'string' || !windowParts) {
    return null;
  }

  const [, startHour, startMinute, endHour, endMinute] = windowParts;
  const startMinutes = Number(startHour) * 60 + Number(startMinute);
  const endMinutes = Number(endHour) * 60 + Number(endMinute);
  const hasValidTimeParts =
    Number(startHour) <= 23 &&
    Number(endHour) <= 23 &&
    Number(startMinute) <= 59 &&
    Number(endMinute) <= 59;
  const requestedDay = moment.tz(
    requestedDate,
    'YYYY-MM-DD',
    true,
    CLIENT_OPPORTUNITY_TIME_ZONE
  );

  if (!hasValidTimeParts || !requestedDay.isValid()) {
    return null;
  }

  const end = requestedDay
    .clone()
    .hour(Number(endHour))
    .minute(Number(endMinute))
    .second(0)
    .millisecond(0);

  return endMinutes <= startMinutes ? end.add(1, 'day').toDate() : end.toDate();
};

export const isClientOpportunityCurrent = (listing, now = new Date()) => {
  const end = getClientOpportunityEnd(listing);
  const nowTimestamp = now instanceof Date ? now.getTime() : new Date(now).getTime();

  return end != null && Number.isFinite(nowTimestamp) && nowTimestamp < end.getTime();
};

export const isClientOpportunityPast = (listing, now = new Date()) => {
  const end = getClientOpportunityEnd(listing);
  const nowTimestamp = now instanceof Date ? now.getTime() : new Date(now).getTime();

  return end != null && Number.isFinite(nowTimestamp) && nowTimestamp >= end.getTime();
};

export const getTransactionState = transaction => {
  const explicitState = normalizeState(transaction?.attributes?.state);
  if (explicitState) {
    return explicitState;
  }

  const processName = transaction?.attributes?.processName;
  const processVersion = transaction?.attributes?.processVersion;
  try {
    const derivedState = processName
      ? normalizeState(getProcess(processName, processVersion)?.getState(transaction))
      : null;
    return derivedState || normalizeState(transaction?.attributes?.lastTransition) || null;
  } catch (error) {
    return normalizeState(transaction?.attributes?.lastTransition) || null;
  }
};

/**
 * Classify a customer opportunity listing using every associated transaction.
 * Only a confirmed, non-terminal transaction makes an opportunity active.
 * Unknown states are retained for diagnostics, but never imply acceptance.
 */
export const classifyClientOpportunityListing = ({ listing, transactions = [], now }) => {
  if (!isClientOpportunityListing(listing)) {
    return null;
  }

  const transactionStates = transactions.map(getTransactionState);
  const unknownStates = transactionStates.filter(
    state => !state || !KNOWN_TRANSACTION_STATES.includes(state)
  );
  const hasInProcessTransaction = transactionStates.some(state =>
    IN_PROCESS_TRANSACTION_STATES.includes(state)
  );

  if (hasInProcessTransaction) {
    return {
      group: LISTING_GROUP_ACTIVE,
      status: LISTING_STATUS_IN_PROCESS,
      transactionStates,
      unknownStates,
    };
  }

  if (transactionStates.some(state => COMPLETED_TRANSACTION_STATES.includes(state))) {
    return {
      group: LISTING_GROUP_ARCHIVED,
      status: LISTING_STATUS_COMPLETED,
      transactionStates,
      unknownStates,
    };
  }

  if (transactionStates.includes('canceled')) {
    return {
      group: LISTING_GROUP_ARCHIVED,
      status: LISTING_STATUS_CANCELED,
      transactionStates,
      unknownStates,
    };
  }

  if (transactionStates.includes('payment-expired')) {
    return {
      group: LISTING_GROUP_ARCHIVED,
      status: LISTING_STATUS_EXPIRED,
      transactionStates,
      unknownStates,
    };
  }

  const listingState = listing?.attributes?.state;
  if (listingState === 'closed') {
    return {
      group: LISTING_GROUP_ARCHIVED,
      status: LISTING_STATUS_MANUALLY_ARCHIVED,
      transactionStates,
      unknownStates,
    };
  }

  if (
    ['draft', 'pendingApproval'].includes(listingState) &&
    listing?.attributes?.privateData?.[ARCHIVED_BY_AUTHOR_FIELD] === true
  ) {
    return {
      group: LISTING_GROUP_ARCHIVED,
      status: LISTING_STATUS_MANUALLY_ARCHIVED,
      transactionStates,
      unknownStates,
    };
  }

  if (listingState === 'published' && isClientOpportunityPast(listing, now)) {
    return {
      group: LISTING_GROUP_AVAILABLE,
      status: LISTING_STATUS_PAST_DATE,
      transactionStates,
      unknownStates,
    };
  }

  if (listingState === 'published' && !isClientOpportunityCurrent(listing, now)) {
    return {
      group: LISTING_GROUP_ARCHIVED,
      status: LISTING_STATUS_EXPIRED,
      transactionStates,
      unknownStates,
    };
  }

  const statusByListingState = {
    draft: LISTING_STATUS_DRAFT,
    pendingApproval: LISTING_STATUS_PENDING_APPROVAL,
    published: LISTING_STATUS_RECEIVING_OFFERS,
  };
  const knownListingStatus = statusByListingState[listingState];

  return {
    group: knownListingStatus ? LISTING_GROUP_AVAILABLE : LISTING_GROUP_ACTIVE,
    status: knownListingStatus || LISTING_STATUS_UNKNOWN,
    transactionStates,
    unknownStates: knownListingStatus ? unknownStates : [...unknownStates, listingState || null],
  };
};
